import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BellRing, Loader2, Save, Send } from "lucide-react";
import { toast } from "sonner";

type TestResult = {
  channel: "slack" | "email";
  status: "sent" | "skipped" | "error";
  detail?: string;
  latency_ms?: number;
};


type Settings = {
  id: string;
  enabled: boolean;
  threshold: number;
  window_minutes: number;
  cooldown_minutes: number;
  slack_webhook_url: string | null;
  notify_email: string | null;
};

type AlertRow = {
  id: string;
  event_count: number;
  threshold: number;
  window_minutes: number;
  window_start: string;
  breakdown: Array<{ action: string; field: string; reason_code: string; count: number }> | null;
  slack_status: string | null;
  email_status: string | null;
  created_at: string;
};

export function TrendSanitizeAlertSettingsPanel() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<TestResult[] | null>(null);
  const [testedAt, setTestedAt] = useState<string | null>(null);


  const load = async () => {
    setLoading(true);
    const [{ data: s, error: sErr }, { data: a }] = await Promise.all([
      supabase
        .from("trend_sanitize_alert_settings" as any)
        .select("id, enabled, threshold, window_minutes, cooldown_minutes, slack_webhook_url, notify_email")
        .limit(1)
        .maybeSingle(),
      supabase
        .from("trend_sanitize_alerts" as any)
        .select(
          "id, event_count, threshold, window_minutes, window_start, breakdown, slack_status, email_status, created_at",
        )
        .order("created_at", { ascending: false })
        .limit(20),
    ]);
    setLoading(false);
    if (sErr) return toast.error(`Load alert settings: ${sErr.message}`);
    setSettings((s ?? null) as unknown as Settings | null);
    setAlerts((a ?? []) as unknown as AlertRow[]);
  };

  useEffect(() => {
    void load();
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    const { error } = await supabase
      .from("trend_sanitize_alert_settings" as any)
      .update({
        enabled: settings.enabled,
        threshold: settings.threshold,
        window_minutes: settings.window_minutes,
        cooldown_minutes: settings.cooldown_minutes,
        slack_webhook_url: settings.slack_webhook_url || null,
        notify_email: settings.notify_email || null,
      })
      .eq("id", settings.id);
    setSaving(false);
    if (error) return toast.error(`Save failed: ${error.message}`);
    toast.success("Sanitization alert settings saved");
  };

  const patch = (p: Partial<Settings>) => setSettings((s) => (s ? { ...s, ...p } : s));

  const sendTest = async () => {
    setTesting(true);
    setTestResults(null);
    const { data, error } = await supabase.functions.invoke("trend-sanitize-alert-test", { body: {} });
    setTesting(false);

    if (error) {
      const detail = (error as { context?: { text?: () => Promise<string> } }).context?.text
        ? await (error as { context: { text: () => Promise<string> } }).context.text()
        : error.message;
      setTestResults([{ channel: "slack", status: "error", detail }]);
      return toast.error(`Test alert failed: ${detail}`);
    }

    const results = ((data as { results?: TestResult[] })?.results ?? []) as TestResult[];
    setTestResults(results);
    setTestedAt(new Date().toLocaleString());

    const sent = results.filter((r) => r.status === "sent").map((r) => r.channel);
    const failed = results.filter((r) => r.status === "error");
    if (failed.length) {
      toast.error(`Test alert: ${failed.map((f) => `${f.channel} failed`).join(", ")}`);
    } else if (sent.length) {
      toast.success(`Test alert sent via ${sent.join(" and ")}`);
    } else {
      toast.info("No Slack webhook or notification email is configured yet");
    }
  };


  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BellRing className="size-4" /> Sanitization alert threshold
        </CardTitle>
        <CardDescription>
          Notify admins (and optionally Slack) when blocked or sanitized trend inputs exceed a threshold inside a
          rolling time window — the signature of a sustained prompt-injection attempt in the source signals.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <Loader2 className="size-4 animate-spin" />}

        {!loading && !settings && (
          <p className="text-sm text-muted-foreground">No alert settings row is visible for your account.</p>
        )}

        {settings && (
          <>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label htmlFor="sanitize-alerts-enabled">Alerts enabled</Label>
              <Switch
                id="sanitize-alerts-enabled"
                checked={settings.enabled}
                onCheckedChange={(v) => patch({ enabled: v })}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="sanitize-threshold">Threshold (events)</Label>
                <Input
                  id="sanitize-threshold"
                  type="number"
                  min={1}
                  value={settings.threshold}
                  onChange={(e) => patch({ threshold: Math.max(1, Number(e.target.value) || 1) })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sanitize-window">Window (minutes)</Label>
                <Input
                  id="sanitize-window"
                  type="number"
                  min={1}
                  value={settings.window_minutes}
                  onChange={(e) => patch({ window_minutes: Math.max(1, Number(e.target.value) || 1) })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sanitize-cooldown">Cooldown (minutes)</Label>
                <Input
                  id="sanitize-cooldown"
                  type="number"
                  min={0}
                  value={settings.cooldown_minutes}
                  onChange={(e) => patch({ cooldown_minutes: Math.max(0, Number(e.target.value) || 0) })}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="sanitize-slack">Slack webhook URL (optional)</Label>
                <Input
                  id="sanitize-slack"
                  value={settings.slack_webhook_url ?? ""}
                  placeholder="https://hooks.slack.com/services/…"
                  onChange={(e) => patch({ slack_webhook_url: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sanitize-email">Notification email (optional)</Label>
                <Input
                  id="sanitize-email"
                  type="email"
                  value={settings.notify_email ?? ""}
                  placeholder="security@example.com"
                  onChange={(e) => patch({ notify_email: e.target.value })}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => void save()} disabled={saving} size="sm">
                {saving ? <Loader2 className="size-3.5 mr-1 animate-spin" /> : <Save className="size-3.5 mr-1" />}
                Save settings
              </Button>
              <Button onClick={() => void sendTest()} disabled={testing} size="sm" variant="outline">
                {testing ? <Loader2 className="size-3.5 mr-1 animate-spin" /> : <Send className="size-3.5 mr-1" />}
                Send test alert
              </Button>
              <span className="text-xs text-muted-foreground">
                Uses the saved settings — save first if you just changed the webhook or email.
              </span>
            </div>

            {testResults && (
              <div
                role="status"
                aria-live="polite"
                className="space-y-1 rounded-lg border border-border p-3 text-xs"
              >
                <div className="font-medium">
                  Test delivery results{testedAt ? ` · ${testedAt}` : ""}
                </div>
                {testResults.map((r) => (
                  <div key={r.channel} className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={
                        r.status === "sent" ? "default" : r.status === "error" ? "destructive" : "outline"
                      }
                    >
                      {r.channel}: {r.status}
                    </Badge>
                    {typeof r.latency_ms === "number" && (
                      <span className="text-muted-foreground">{r.latency_ms} ms</span>
                    )}
                    {r.detail && <span className="text-muted-foreground break-all">{r.detail}</span>}
                  </div>
                ))}
                <p className="text-muted-foreground">
                  Test sends never start a cooldown window, so real alerts stay unaffected.
                </p>
              </div>
            )}

          </>
        )}

        {alerts.length > 0 && (
          <Alert>
            <BellRing className="size-4" />
            <AlertTitle>Recent alerts</AlertTitle>
            <AlertDescription className="space-y-2">
              {alerts.map((a) => (
                <div key={a.id} className="text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="destructive">{a.event_count} events</Badge>
                    <span className="text-muted-foreground">
                      threshold {a.threshold} · {a.window_minutes}m window ·{" "}
                      {new Date(a.created_at).toLocaleString()}
                    </span>
                    {a.slack_status && <Badge variant="outline">slack: {a.slack_status}</Badge>}
                    {a.email_status && <Badge variant="outline">email: {a.email_status}</Badge>}
                  </div>
                  {a.breakdown?.length ? (
                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                      {a.breakdown
                        .slice(0, 5)
                        .map((b) => `${b.count}× ${b.action} ${b.field}/${b.reason_code}`)
                        .join(" · ")}
                    </div>
                  ) : null}
                </div>
              ))}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default TrendSanitizeAlertSettingsPanel;
